import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastService } from '@msi/cobalt';
import { BehaviorSubject, Observable, Subject, Subscription, timer } from 'rxjs';
import { map, share, take, takeWhile } from 'rxjs/operators';
import { NodeData } from 'src/app/interfaces/node-models.interface';
import { ObjID } from 'src/app/interfaces/object-id.interface';
import { AclUserModel } from 'src/app/interfaces/user.interface';
import { ViewModel, WorkspaceDisableRule, WorkspaceDisableTimerInfo } from 'src/app/interfaces/views.interface';
import { notNull } from 'src/app/utils/rxjs-custom-operators/rxjs-custom-operators.utils';
import { workspacesAlphabetically } from 'src/app/utils/sorters/sorters.utils';
import { ApiService } from '../api/api.service';
import { CcAdminApiService } from '../ccAdmin/cc-admin-api.service';
import { LoadingErrorsService } from '../loading-errors/loading-errors.service';
import { viewCriteriaModelArrayToMap } from './views.utils';
import { SignalRService } from '../signalr/signalr.service';

@Injectable({
	providedIn: 'root',
})
export class ViewsService {
	readonly noPermission = $localize`You do not have the permission.`;

	/** Observable to track the currently active view */
	public activeViewId: ObjID = null;
	private activeViewSubject: BehaviorSubject<ObjID> = new BehaviorSubject<ObjID>(null);
	public get activeView$(): Observable<ObjID> {
		return this.activeViewSubject.asObservable();
	}

	/** Observable to track the list of all views */
	private viewMap: Map<ObjID, ViewModel> = new Map();
	private viewsSubject: BehaviorSubject<ViewModel[]> = new BehaviorSubject<ViewModel[]>(null);
	public get views$(): Observable<ViewModel[]> {
		return this.viewsSubject.asObservable().pipe(notNull());
	}

	/** Observable to emit when a view is deleted */
	private viewDeletedSubject: Subject<ObjID> = new Subject<ObjID>();
	public get viewDeleted$(): Observable<ObjID> {
		return this.viewDeletedSubject.asObservable();
	}

	/** Observable to emit workspace disable timers */
	private workspaceDisableTimersMap: Map<ObjID, WorkspaceDisableTimerInfo> = new Map();
	private workspaceDisableTimersSubject: BehaviorSubject<Map<string, WorkspaceDisableTimerInfo>> = new BehaviorSubject(
		null
	);
	public get workspaceDisableTimers$(): Observable<Map<string, WorkspaceDisableTimerInfo>> {
		return this.workspaceDisableTimersSubject.asObservable().pipe(notNull());
	}
	private workspaceDisableTimerSubscriptions: Subscription[];

	constructor(
		private apiService: ApiService,
		private ccAdminApiService: CcAdminApiService,
		private toastService: ToastService,
		private loadingErrorsService: LoadingErrorsService,
		private router: Router,
		private signalRService: SignalRService
	) {
		this.ccAdminApiService.graphInitialized$.pipe(take(1)).subscribe(() => this.loadViews());
		this.signalRService.workspaceDisableTriggered$.subscribe(workspaceId => {
			this.setWorkspaceDisableTimer(workspaceId);
		});
	}

	/**
	 * Sets a view as the active view
	 * @param viewId - ID of the view
	 */
	public setActiveView(viewId: ObjID) {
		this.views$.pipe(take(1)).subscribe(() => {
			// If view does not exist, default to "All" and clear query params
			if (!this.viewMap.has(viewId) || viewId == null) {
				viewId = null;
			}
			// Preserve active workspaceId as a query param
			this.router.navigate([], {
				queryParams: { workspaceId: viewId },
				replaceUrl: true,
				queryParamsHandling: 'merge',
			});
			this.activeViewId = viewId;
			this.activeViewSubject.next(viewId);
			localStorage.setItem('last-active-workspace', viewId ?? '');
		});
	}

	/**
	 * Gets a view from the viewMap
	 * @param viewId - ID of the view
	 * @returns - The view corresponding to viewId
	 */
	public getView(viewId: ObjID) {
		return this.viewMap.get(viewId);
	}

	/**
	 * Deletes an existing view
	 * @param viewId - ID of the view to be deleted
	 */
	public deleteView(viewId: ObjID) {
		const request = this.apiService.deleteView(viewId).pipe(share());
		request.subscribe({
			next: () => {
				this.viewMap.delete(viewId);
				this.viewDeletedSubject.next(viewId);
				// If the deleted workspace was the active workspace, send user back to "ALL"
				if (this.activeViewId === viewId) {
					this.setActiveView(null);
				}
				this.emitViews();
			},
			error: () => {
				this.toastService.error(
					$localize`Failed to delete workspace. This may still have deleted some of its workflows. Try again.`,
					null,
					{ autoDismiss: 14000 }
				);
			},
		});
		return request;
	}

	/**
	 * Creates or updates a view
	 * @param view - View to be created
	 */
	public putView(view: ViewModel) {
		this.apiService.putView(view).subscribe({
			next: response => {
				this.viewMap.set(view.id, response);
				this.emitViews();
				this.setActiveView(view.id);
			},
			error: () => {
				this.toastService.error($localize`Failed to update workspace.`, null, { autoDismiss: 14000 });
			},
		});
	}

	/**
	 * Enable ACL
	 */
	public enableAcl(viewId: ObjID) {
		return this.apiService.enableAcl(viewId).subscribe({
			next: response => {
				this.viewMap.set(viewId, response);
				this.emitViews();
			},
			error: () => {
				this.toastService.error($localize`Failed to make workspace private.`, null, { autoDismiss: 14000 });
			},
		});
	}

	/**
	 * Disable ACL
	 */
	public disableAcl(viewId: ObjID) {
		return this.apiService.disableAcl(viewId).subscribe({
			next: response => {
				this.viewMap.set(viewId, response);
				this.emitViews();
			},
			error: () => {
				this.toastService.error($localize`Failed to make workspace public.`, null, { autoDismiss: 14000 });
			},
		});
	}

	public reloadView(workspaceId: string) {
		const request = this.apiService.getView(workspaceId).pipe(share());
		request.subscribe({
			next: workspace => {
				this.viewMap.set(workspaceId, workspace);
				this.emitViews();
			},
			error: (err: HttpErrorResponse) => {
				if (err.status === 403 || err.status === 404) {
					this.viewMap.delete(workspaceId);
					this.emitViews();
				} else {
					this.toastService.error($localize`Failed to re-load workspace`);
				}
			},
		});
		return request;
	}

	/**
	 * Generates a lambda function which can be used to filter a list of NodeData
	 * Only NodeData which are part of the current view will be remain
	 * @returns - the filter function
	 */
	public getFilter(): (n: NodeData) => boolean {
		const criteria = this.viewMap.get(this.activeViewSubject.value)?.criteria;
		if (!criteria) {
			return () => true;
		}
		const selectedCriteriaSet = viewCriteriaModelArrayToMap(criteria);

		return (nodeData: NodeData) => {
			if (selectedCriteriaSet.has({ product: nodeData.appId, key: null, value: null })) {
				return true;
			}
			return nodeData.metadataList?.some(kvp =>
				selectedCriteriaSet.has({ product: nodeData.appId, key: kvp.key, value: kvp.value })
			);
		};
	}

	/**
	 * Calls the API service to add a user into the ACL list
	 * @param user - user object that is to be added
	 * @param workspaceId - ID for the workspace where the user is to be added
	 */
	public addUser(user: AclUserModel, workspaceId: string) {
		return this.apiService.addUser(user, workspaceId).subscribe({
			next: workspace => {
				if (workspace) {
					this.viewMap.set(workspace.id, workspace);
					this.emitViews();
				}
			},
			error: err => {
				this.toastService.error(
					$localize`Failed to add user: ${user.firstName} ${user.lastName}. ${
						err.status === 403 ? this.noPermission : ''
					}`,
					null,
					{
						autoDismiss: 14000,
					}
				);
			},
		});
	}

	/**
	 * Calls the API service to remove a user from the ACL list
	 * @param user - user object that is to be removed
	 * @param workspaceId - ID for the workspace from where the user is to be remove
	 */
	public removeUser(user: AclUserModel, workspaceId: string) {
		return this.apiService.removeUser(user, workspaceId).subscribe({
			next: workspace => {
				// If user remove their own access, delete the workspace
				if (!this.ccAdminApiService.user.canViewWorkspace(workspace)) {
					this.viewMap.delete(workspace.id);
					this.setActiveView(null);
				} else {
					// Otherwise, update the workspace based on latest data
					this.viewMap.set(workspace.id, workspace);
				}
				this.emitViews();
			},
			error: err => {
				this.toastService.error(
					`Failed to remove user: ${user.firstName} ${user.lastName}. ${err.status === 403 ? this.noPermission : ''}`,
					null,
					{
						autoDismiss: 14000,
					}
				);
			},
		});
	}

	/**
	 * Updates a workspace's disable rule
	 * @param workspaceId ID of the workspace to be updated
	 * @param rule - New rule to be set
	 * @returns The latest workspace after document is updated
	 */
	public editWorkspaceDisableRule(workspaceId: string, rule: WorkspaceDisableRule) {
		const request = this.apiService.editWorkspaceDisableRule(workspaceId, rule).pipe(share());
		request.subscribe({
			next: workspace => {
				this.viewMap.set(workspace.id, workspace);
				this.emitViews();
				this.toastService.success($localize`Successfully saved Disable Workspace on Trigger.`, null, {
					autoDismiss: 7000,
				});
			},
			error: err => {
				this.toastService.error(
					$localize`Failed to save Disable Workspace on Trigger. ${err.status === 403 ? this.noPermission : ''}`,
					null,
					{
						autoDismiss: 14000,
					}
				);
			},
		});
		return request;
	}

	/**
	 * Deletes a workspace's disable rule
	 * @param workspaceId ID of the workspace to be updated
	 * @returns The latest workspace after document is updated
	 */
	public deleteWorkspaceDisableRule(workspaceId: string) {
		const request = this.apiService.deleteWorkspaceDisableRule(workspaceId).pipe(share());
		request.subscribe({
			next: workspace => {
				this.viewMap.set(workspace.id, workspace);
				this.emitViews();
				this.toastService.success($localize`Successfully deleted disable workspace on trigger setting.`, null, {
					autoDismiss: 7000,
				});
			},
			error: err => {
				this.toastService.error(
					$localize`Failed to delete Disable Workspace on Trigger setting. ${
						err.status === 403 ? this.noPermission : ''
					}`,
					null,
					{
						autoDismiss: 14000,
					}
				);
			},
		});
		return request;
	}

	/**
	 * Cancels the timer on a presently disabled workspace and re-enable the workspace
	 * @param workspaceId ID of the workspace whose timer is to be canceled
	 * @returns Whether or not the timer needed to be canceled
	 */
	public cancelWorkspaceDisableTimer(workspaceId: string) {
		const request = this.apiService.cancelWorkspaceDisableTimer(workspaceId).pipe(share());
		request.subscribe({
			next: result => {
				this.workspaceDisableTimersMap.delete(workspaceId);
				this.emitWorkspaceDisableTimers();
				this.toastService.success(
					result.canceled
						? $localize`Workspace successfully re-enabled.`
						: $localize`Workspace has already been re-enabled.`,
					null,
					{
						autoDismiss: 7000,
					}
				);
			},
			error: err => {
				this.toastService.error(
					$localize`Failed to re-enable workspace. ${err.status === 403 ? this.noPermission : ''}`,
					null,
					{
						autoDismiss: 14000,
					}
				);
			},
		});
		return request;
	}

	/**
	 * Update the timer on a presently disabled workspace
	 * @param workspaceId ID of the workspace whose timer is to be canceled
	 * @returns Whether or not the timer has updated
	 */
	public updateWorkspaceDisableTimer(workspaceId: string, minutes: number) {
		const request = this.apiService.updateWorkspaceDisableTimer(workspaceId, minutes).pipe(share());
		request.subscribe({
			next: result => {
				if (result.updated) {
					this.toastService.success($localize`Workspace timer has been successfully updated.`, null, {
						autoDismiss: 7000,
					});
				} else {
					this.toastService.warning($localize`Could not extend timer because it has already completed.`, null, {
						autoDismiss: 7000,
					});
				}
			},
			error: err => {
				this.toastService.error(
					$localize`Failed to update workspace timer. ${err.status === 403 ? this.noPermission : ''}`,
					null,
					{
						autoDismiss: 14000,
					}
				);
			},
		});
		return request;
	}

	/**
	 * Starts/edits/stops a timer for the workspace disable rule based on the API call for `getWorkspaceDisableTimer` from the SignalR message
	 * @param workspaceId Workspace Id for which the timer has been started/edited/stopped
	 */
	public setWorkspaceDisableTimer(workspaceId: string) {
		this.apiService.getWorkspaceDisableTimer(workspaceId).subscribe({
			next: (timerInfo: WorkspaceDisableTimerInfo) => {
				if (timerInfo) {
					this.workspaceDisableTimersMap.set(workspaceId, {
						...timerInfo,
						timeRemaining$: this.generateTimerObservable(timerInfo),
					});
				} else {
					this.workspaceDisableTimersMap.delete(workspaceId);
				}
				this.emitWorkspaceDisableTimers();
			},
		});
	}

	/**
	 * Loads the list of views in this agency
	 */
	private loadViews() {
		this.apiService.getViews().subscribe({
			next: views => {
				this.viewMap = new Map(views.map(v => [v.id, v]));
				this.emitViews();

				// Also load the timerInfo for each disabled workspace
				this.apiService
					.getWorkspaceDisableTimers(views.filter(v => !v.enabled).map(v => v.id))
					.subscribe((timers: WorkspaceDisableTimerInfo[]) => {
						this.workspaceDisableTimersMap = new Map(
							timers
								.filter(t => !!t) // Will be null if no timer is running
								.map(t => [
									t.workspaceId,
									{
										...t,
										timeRemaining$: this.generateTimerObservable(t),
									},
								])
						);
						this.emitWorkspaceDisableTimers();
					});
			},
			error: err => {
				this.loadingErrorsService.openErrorPage(
					err?.status?.toString(),
					err?.statusText,
					$localize`Failed to load workspaces. If issues persist, contact your system administrator.`
				);
			},
		});
	}

	/**
	 * Emit view changes to subscribers
	 */
	private emitViews() {
		Array.from(this.viewMap.values()).forEach(workspace => {
			// Set if user can edit, assign, or delete on a per-workspace basis
			workspace.canAssign = this.ccAdminApiService.user.canAssignWorkspace(workspace);
			workspace.canEdit = this.ccAdminApiService.user.canEditWorkspace(workspace);
			workspace.canEditWorkflowsIn = this.ccAdminApiService.user.canEditWorkflowsInWorkspace(workspace);
			workspace.canDelete = this.ccAdminApiService.user.canDeleteWorkspace();
		});
		this.viewsSubject.next(Array.from(this.viewMap.values()).sort(workspacesAlphabetically));
		this.activeViewSubject.next(this.activeViewSubject.value);
	}

	/**
	 * Emit workspace disable timer changes to subscribers
	 */
	private emitWorkspaceDisableTimers() {
		// Clear previous subscriptions then re-subscribe to each timer to remove it from the map when it completes
		this.workspaceDisableTimerSubscriptions?.forEach(sub => sub.unsubscribe());
		this.workspaceDisableTimerSubscriptions = [...this.workspaceDisableTimersMap.values()].map(timerInfo =>
			timerInfo.timeRemaining$.subscribe({
				complete: () => {
					this.workspaceDisableTimersMap.delete(timerInfo.workspaceId);
					this.workspaceDisableTimersSubject.next(this.workspaceDisableTimersMap);
				},
			})
		);
		// Emit timers
		this.workspaceDisableTimersSubject.next(this.workspaceDisableTimersMap);
	}

	/**
	 * Create an observable which emits the time remaining until the workspace is re-enabled.
	 * The number of milliseconds remaining is recomputed and emitted every second.
	 * @param timerInfo - TimerInfo which specifies the start time and duration of the timer
	 * @returns - The generated observable
	 */
	private generateTimerObservable(timerInfo: WorkspaceDisableTimerInfo): Observable<number> {
		return timer(0, 1000).pipe(
			map(() => new Date(timerInfo.startTime).getTime() - new Date().getTime() + timerInfo.duration * 60 * 1000),
			takeWhile(n => n >= 0) // Stop emitting when timer reaches 0
		);
	}
}

// Copyright (c) Motorola Solutions Inc. All Rights Reserved.
